import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FiAlertTriangle, FiUser, FiDollarSign } from 'react-icons/fi';
import tesoreriaService from '../services/tesoreriaService';
import Loading from '../components/Loading';
import './CuentasPorCobrarPage.css';

const ensureArray = (v) => Array.isArray(v) ? v : [];

const CuentasPorCobrarPage = () => {
  const [facturas, setFacturas] = useState([]);
  const [aging, setAging] = useState(null);
  const [loading, setLoading] = useState(true);
  const [vista, setVista] = useState('lista'); // lista | aging | cliente
  const [clienteId, setClienteId] = useState('');
  const [estadoCuenta, setEstadoCuenta] = useState(null);

  useEffect(() => { cargarDatos(); }, []);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      const [facturasRes, agingRes] = await Promise.all([
        tesoreriaService.cuentasPorCobrar.getAll(),
        tesoreriaService.cuentasPorCobrar.getAging(),
      ]);
      setFacturas(ensureArray(facturasRes.data.results || facturasRes.data));
      setAging(agingRes.data);
    } catch { toast.error('Error al cargar cuentas por cobrar'); }
    finally { setLoading(false); }
  };

  const cargarEstadoCuenta = async (id) => {
    if (!id) return;
    try {
      const res = await tesoreriaService.cuentasPorCobrar.getEstadoCuenta(id);
      setEstadoCuenta(res.data);
      setClienteId(id);
    } catch { toast.error('Error al cargar estado de cuenta'); }
  };

  const formatCurrency = (v) => new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(v || 0);

  const getDiasColor = (dias) => {
    if (dias <= 0) return '#059669';
    if (dias <= 30) return '#f59e0b';
    if (dias <= 60) return '#ea580c';
    return '#dc2626';
  };

  if (loading) return <Loading />;

  const totalPendiente = facturas.reduce((s, f) => s + parseFloat(f.saldo_pendiente || 0), 0);
  const facturasVencidas = facturas.filter(f => f.dias_vencido > 0);

  return (
    <div className="cxc-page">
      <div className="page-header">
        <h1>Cuentas por Cobrar</h1>
        <div className="view-toggle">
          <button className={vista === 'lista' ? 'active' : ''} onClick={() => setVista('lista')}>Lista</button>
          <button className={vista === 'aging' ? 'active' : ''} onClick={() => setVista('aging')}>Aging</button>
          <button className={vista === 'cliente' ? 'active' : ''} onClick={() => setVista('cliente')}>Por Cliente</button>
        </div>
      </div>

      <div className="estadisticas-grid">
        <div className="stat-card" style={{ borderLeftColor: '#3b82f6' }}>
          <div className="stat-label">Total Pendiente</div>
          <div className="stat-value">{formatCurrency(totalPendiente)}</div>
        </div>
        <div className="stat-card" style={{ borderLeftColor: '#dc2626' }}>
          <div className="stat-label">Facturas Vencidas</div>
          <div className="stat-value">{facturasVencidas.length}</div>
        </div>
        <div className="stat-card" style={{ borderLeftColor: '#f59e0b' }}>
          <div className="stat-label">Total Facturas</div>
          <div className="stat-value">{facturas.length}</div>
        </div>
        <div className="stat-card" style={{ borderLeftColor: '#8b5cf6' }}>
          <div className="stat-label">Promedio Dias Cobro</div>
          <div className="stat-value">{facturas.length > 0 ? Math.round(facturas.reduce((s, f) => s + (f.dias_vencido || 0), 0) / facturas.length) : 0}</div>
        </div>
      </div>

      {vista === 'lista' && (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>N° Documento</th>
                <th>Cliente</th>
                <th>Fecha Emision</th>
                <th>Fecha Vencimiento</th>
                <th style={{ textAlign: 'right' }}>Monto Total</th>
                <th style={{ textAlign: 'right' }}>Pagado</th>
                <th style={{ textAlign: 'right' }}>Saldo</th>
                <th>Dias Vencido</th>
              </tr>
            </thead>
            <tbody>
              {facturas.length === 0 ? (
                <tr><td colSpan="8" className="empty-state"><p>No hay facturas pendientes de cobro</p></td></tr>
              ) : facturas.map((f, i) => (
                <tr key={i}>
                  <td className="numero-doc">{f.numero_documento || f.numero || '-'}</td>
                  <td>{f.cliente_nombre || f.cliente?.nombre || '-'}</td>
                  <td>{f.fecha_emision || f.fecha}</td>
                  <td>{f.fecha_vencimiento}</td>
                  <td style={{ textAlign: 'right', fontFamily: "'Courier New', monospace" }}>{formatCurrency(f.monto_total)}</td>
                  <td style={{ textAlign: 'right', fontFamily: "'Courier New', monospace", color: '#059669' }}>{formatCurrency(f.monto_pagado || 0)}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600, fontFamily: "'Courier New', monospace" }}>{formatCurrency(f.saldo_pendiente)}</td>
                  <td>
                    <span className="dias-badge" style={{ color: getDiasColor(f.dias_vencido || 0) }}>
                      {f.dias_vencido > 0 && <FiAlertTriangle size={14} />} {f.dias_vencido || 0} dias
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {vista === 'aging' && aging && (
        <div className="aging-container">
          <div className="aging-grid">
            {[
              { label: 'Vigente', key: 'vigente', color: '#059669' },
              { label: '1-30 dias', key: '1_30', color: '#f59e0b' },
              { label: '31-60 dias', key: '31_60', color: '#ea580c' },
              { label: '61-90 dias', key: '61_90', color: '#dc2626' },
              { label: '90+ dias', key: '90_plus', color: '#7f1d1d' },
            ].map(tramo => (
              <div key={tramo.key} className="aging-card" style={{ borderTopColor: tramo.color }}>
                <div className="aging-label">{tramo.label}</div>
                <div className="aging-value" style={{ color: tramo.color }}>{formatCurrency(aging[tramo.key]?.total || 0)}</div>
                <div className="aging-count">{aging[tramo.key]?.cantidad || 0} facturas</div>
              </div>
            ))}
          </div>
          <div className="aging-total">
            <strong>Total Cartera:</strong> {formatCurrency(aging.total || totalPendiente)}
          </div>
        </div>
      )}

      {vista === 'cliente' && (
        <div className="cliente-container">
          <div className="form-group" style={{ maxWidth: 400, marginBottom: 24 }}>
            <label>Seleccionar Cliente</label>
            <input type="number" className="input-filtro" placeholder="ID del cliente" value={clienteId} onChange={e => setClienteId(e.target.value)} onKeyDown={e => e.key === 'Enter' && cargarEstadoCuenta(clienteId)} />
            <button className="btn-primary" style={{ marginTop: 8 }} onClick={() => cargarEstadoCuenta(clienteId)}>Consultar</button>
          </div>
          {estadoCuenta && (
            <div className="estado-cuenta-card">
              <h3><FiUser /> {estadoCuenta.cliente_nombre || 'Cliente'}</h3>
              <div className="estado-cuenta-resumen">
                <div><span>Total Facturado:</span><strong>{formatCurrency(estadoCuenta.total_facturado)}</strong></div>
                <div><span>Total Pagado:</span><strong style={{ color: '#059669' }}>{formatCurrency(estadoCuenta.total_pagado)}</strong></div>
                <div><span>Saldo Pendiente:</span><strong style={{ color: '#dc2626' }}>{formatCurrency(estadoCuenta.saldo_pendiente)}</strong></div>
              </div>
              {estadoCuenta.facturas && (
                <table className="data-table" style={{ marginTop: 16 }}>
                  <thead><tr><th>Documento</th><th>Fecha</th><th style={{ textAlign: 'right' }}>Monto</th><th style={{ textAlign: 'right' }}>Pagado</th><th style={{ textAlign: 'right' }}>Saldo</th></tr></thead>
                  <tbody>
                    {estadoCuenta.facturas.map((f, i) => (
                      <tr key={i}>
                        <td>{f.numero_documento || '-'}</td>
                        <td>{f.fecha}</td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(f.monto_total)}</td>
                        <td style={{ textAlign: 'right', color: '#059669' }}>{formatCurrency(f.monto_pagado)}</td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatCurrency(f.saldo_pendiente)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CuentasPorCobrarPage;
